import React, { useCallback, useRef, useState } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';

import { api, lang, remCss, rgba, scssVariables } from 'src/helpers';
import SVGIcon, { BASE_ICONS } from '../svg';

const caches = {};

export const AsyncSelectField = ({
  template,
  label,
  name,
  value,
  onChange,
  placeholder,
  apiConfig,
  initSelectedParamKey,
  defaultValue = false,
  invalid = {},
  width = '100%',
  disabled = false,
  isClearable = true,
  extendOptions = [],
  isMultiple = false,
  allowNew = false,
  canProcess = null,
  cacheOptionId,
  ...otherProps
}) => {
  const [typingTimer, setTypingTimer] = useState();
  const [startedDefault, setStartedDefault] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const selectRef = useRef();
  const styleDefault = {
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: remCss(8),
      svg: {
        width: remCss(20),
        height: remCss(20)
      }
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: remCss(8),
      svg: {
        width: remCss(20),
        height: remCss(20)
      }
    }),
    valueContainer: (s) => ({
      ...s,
      padding: `${remCss(2)}px ${remCss(8)}px`
    })
  };

  const styles = {
    0: {
      container: (s) => ({ ...s, width: width }),
      control: (s) => ({
        ...s,
        ':hover': {
          border: 'none'
        },

        border: 'none',
        paddingTop: remCss(5),
        paddingBottom: remCss(5),
        paddingLeft: remCss(4),
        paddingRight: remCss(4)
      }),
      option: (s, { isSelected }) => ({
        ...s,
        backgroundColor: isSelected
          ? rgba(scssVariables['colors-mc-primary'], 0.5)
          : scssVariables['colors-mc-white']
      }),
      ...styleDefault
    },
    1: {
      container: (s) => ({ ...s, width: width }),
      control: (s) => ({
        ...s,
        ':hover': {
          border: 'none'
        },
        border: 'none',
        padding: 0
      }),
      ...styleDefault
    },
    2: {
      container: (s) => ({ ...s, width: width }),
      control: (s) => ({
        ...s,
        border: 'none',
        backgroundColor: 'transparent',
        cursor: disabled ? 'not-allowed' : 'default',
        ':hover': {
          border: 'none'
        },
        paddingTop: remCss(8),
        paddingBottom: remCss(8),
        paddingLeft: remCss(4),
        paddingRight: remCss(4)
      }),
      ...styleDefault
    },
    3: {
      container: (s) => ({ ...s, width: width }),
      control: (s) => ({
        ...s,
        boxShadow: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: disabled ? 'not-allowed' : 'default',
        ':hover': {
          border: 'none'
        },
        paddingTop: remCss(6),
        paddingBottom: remCss(6),
        paddingLeft: remCss(7),
        paddingRight: remCss(7)
      }),
      multiValue: (styles) => ({
        ...styles,
        backgroundColor: 'white'
      }),
      ...styleDefault
    }
  };
  styles[5] = styles[0];
  styles[6] = styles[0];
  const placeholderTxt = (lang.t(placeholder) || lang.t('Search')) + '...';

  const apiData = (apiConfig, inputValue, cb) => {
    const { url, valueKey, labelKey, valueFn, labelFn, ...params } = apiConfig;

    const request = api(url, {
      skip: 0,
      limit: 10,
      query: inputValue,
      ...params
    });

    request.then(({ error, data }) => {
      if (error === 'cancel') return;

      let items = (data?.items ? data.items : data || []).map((item) => ({
        value: valueFn ? valueFn(item) : valueKey ? item[valueKey] : item.id,
        label: labelFn ? labelFn(item) : labelKey ? item[labelKey] : item.id,
        item
      }));
      cb && cb(items);
    });
  };

  const loadOptions = useCallback(
    (inputValue, cb) => {
      if (canProcess && !canProcess(inputValue)) return cb([]);
      if (!startedDefault && cacheOptionId && caches[cacheOptionId]) {
        let items = [
          ...extendOptions.filter((e) => !caches[cacheOptionId].find((i) => i.value === e.value)),
          ...caches[cacheOptionId]
        ];
        setOptions(items);
        setStartedDefault(true);
        return cb(items);
      }

      if (typingTimer) clearTimeout(typingTimer);

      setTypingTimer(
        setTimeout(() => {
          apiData(apiConfig, inputValue, (list) => {
            if (startedDefault) {
              setOptions(list);
              return cb(list);
            }
            setStartedDefault(true);

            let items = [
              ...extendOptions.filter((e) => !list.find((i) => i.value === e.value)),
              ...list
            ];

            if (initSelectedParamKey && value && !items.find((i) => i.value === value)) {
              apiData({ ...apiConfig, [initSelectedParamKey]: value }, '', (selectedItems) => {
                items = [...selectedItems, ...items];
                if (defaultValue) onChange(name, items[0]?.value, items[0]);
                setOptions(items);
                cb(items);
                caches[cacheOptionId] = items;
              });
            } else {
              if (defaultValue) onChange(name, items[0]?.value, items[0]);
              setOptions(items);
              cb(items);
              caches[cacheOptionId] = items;
            }
          });
        }, 200)
      );
    },
    [
      apiConfig,
      cacheOptionId,
      canProcess,
      defaultValue,
      extendOptions,
      initSelectedParamKey,
      name,
      onChange,
      startedDefault,
      typingTimer,
      value
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && e.target.value) {
        e.preventDefault();
        const hasExactMatch = options.find(
          (option) => option.label.toLowerCase() === e.target.value.toLowerCase()
        );

        if (!hasExactMatch) {
          if (!canProcess || (!!canProcess && canProcess(e.target.value))) {
            const a = { label: e.target.value, value: e.target.value };
            onChange(name, isMultiple ? [a.value] : a.value, isMultiple ? [a] : a);
          }
        } else {
          onChange(
            name,
            isMultiple ? [hasExactMatch.value] : hasExactMatch.value,
            isMultiple ? [hasExactMatch] : hasExactMatch
          );
        }
        setInputValue('');

        // Keep focus on the select
        if (selectRef.current) {
          selectRef.current.focus();
        }
      }
    },
    [canProcess, isMultiple, name, onChange, options]
  );

  let Comp = AsyncSelect;
  if (allowNew) Comp = AsyncCreatableSelect;

  return (
    <div
      className={'mc-select-field-group template-' + template + (allowNew ? ' mc-creatable' : '')}
    >
      {label && <label htmlFor={'mc-' + name}>{lang.t(label)}</label>}
      <Comp
        ref={(ref) => (selectRef.current = ref)}
        inputId={'mc-' + name}
        noOptionsMessage={({ inputValue }) => {
          return inputValue && inputValue.length > 0 ? 'No results' : '';
        }}
        className='basic-single'
        classNamePrefix='select'
        cacheOptions={true}
        value={
          value
            ? isMultiple
              ? options.filter((i) => value.includes(i.value))
              : options?.find((i) => i.value === value)
            : null
        }
        inputValue={inputValue}
        onInputChange={(newValue) => {
          setInputValue(newValue);
        }}
        loadOptions={loadOptions}
        defaultOptions={true}
        placeholder={placeholderTxt}
        onMenuOpen={() => {
          setTimeout(() => {
            const selected = selectRef.current?.menuListRef?.getElementsByClassName(
              'select__option--is-selected'
            );

            if (selected?.length)
              selected[0]?.scrollIntoView({
                behavior: 'instant',
                block: 'center'
              });
          }, 100);
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: scssVariables['colors-mc-white']
          }
        })}
        components={{
          DropdownIndicator: (props) => {
            return (
              <components.DropdownIndicator {...props}>
                <SVGIcon
                  iconname={BASE_ICONS.SEARCH}
                  strokeWidthIcon={0.5}
                  width={20}
                  fill={scssVariables['colors-mc-text']}
                />
              </components.DropdownIndicator>
            );
          },
          Menu: (props) => {
            if (canProcess && !canProcess(props.selectProps.inputValue)) return null;
            return <components.Menu {...props} />;
          }
        }}
        onKeyDown={allowNew ? handleKeyDown : null}
        isMulti={isMultiple}
        isClearable={isClearable}
        onChange={(a) => {
          onChange(name, a && (isMultiple ? a.map((item) => item.value) : a.value), a);
        }}
        menuPlacement={'auto'}
        styles={styles[template]}
        menuPosition={'fixed'}
        {...otherProps}
      />
      {invalid[name] && <p className='mc-form-invalid-text'>{invalid[name]}</p>}
    </div>
  );
};
export default AsyncSelectField;
